import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Modal } from "flowbite-react";
import {
  HiOutlinePencil,
  HiOutlinePlus,
  HiOutlineTrash,
  HiFilter,
  HiOutlineArrowRight,
  HiOutlineArrowSmDown,
  HiOutlineArrowSmUp,
} from "react-icons/hi";
import { IoMdSearch } from "react-icons/io";
import AnimatedToast from "../components/common/AnimatedToast";
import axiosInstance from "../config/axiosInstance";
import { CATEGORIES, PRODUCTS } from "../config/apiConfig";

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);

const parseCurrencyDigits = (digits) => {
  if (!digits) return 0;
  const numeric = Number(digits);
  return Number.isFinite(numeric) ? numeric / 100 : 0;
};

const digitsFromNumber = (value) => {
  if (!value) return "";
  return String(Math.round(Number(value) * 100));
};

const formatDigitsToCurrency = (digits) =>
  formatCurrency(parseCurrencyDigits(digits));

const createProductFilterState = () => ({
  search: "",
  type: "all",
  status: "all",
  categoryId: "",
});

const getInitialSortingState = () => ({
  orderBy: "name",
  order: "asc",
});
const skeletonRows = Array.from({ length: 4 });
const DEFAULT_PAGE_LIMIT = 20;
const getInitialPagination = () => ({
  page: 1,
  limit: DEFAULT_PAGE_LIMIT,
  total: 0,
  totalPages: 1,
  offset: 0,
});

const getInitialFormState = (product) => ({
  name: product?.name || "",
  code: product?.code || "",
  type: product?.type || "Produto",
  description: product?.description || "",
  unit: product?.unit || "",
  categoryId: product?.categoryId ?? product?.category?.id ?? "",
  isActive: product?.isActive !== undefined ? Boolean(product.isActive) : true,
});

const NotificationBanner = ({ notification }) => {
  if (!notification) return null;
  return (
    <div
      className={`rounded-2xl border px-4 py-3 text-sm shadow-sm ${
        {
          success: "border-emerald-200 bg-emerald-50 text-emerald-700",
          danger: "border-orange-200 bg-orange-50 text-orange-700",
        }[notification.type] || "border-slate-200 bg-white text-slate-800"
      }`}
    >
      {notification.message}
    </div>
  );
};

const ProductCatalogPage = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(getInitialFormState());
  const [editingProduct, setEditingProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [animatedToast, setAnimatedToast] = useState(null);
  const [unitPriceRaw, setUnitPriceRaw] = useState("");
  const [filters, setFilters] = useState(createProductFilterState);
  const [filterDraft, setFilterDraft] = useState(createProductFilterState);
  const [sorting, setSorting] = useState(getInitialSortingState);
  const [pagination, setPagination] = useState(getInitialPagination);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [productMenuOpen, setProductMenuOpen] = useState(false);
  const [productMenuSlideIn, setProductMenuSlideIn] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [manageCategoriesModalOpen, setManageCategoriesModalOpen] =
    useState(false);
  const [categoryFormName, setCategoryFormName] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  const [isDeletingCategoryId, setIsDeletingCategoryId] = useState(null);
  const [categoryNotification, setCategoryNotification] = useState(null);

  const loadProducts = useCallback(async () => {
    setError("");
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) {
        params.set("q", filters.search);
      }
      if (filters.type && filters.type !== "all") {
        params.set("type", filters.type);
      }
      if (filters.status && filters.status !== "all") {
        params.set("status", filters.status);
      }
      if (filters.categoryId) {
        params.set("categoryId", filters.categoryId);
      }
      params.set("page", String(pagination.page));
      params.set("limit", String(pagination.limit));
      if (sorting.orderBy) {
        params.set("orderBy", sorting.orderBy);
        params.set("order", sorting.order);
      }
      const url =
        params.toString().length > 0
          ? `${PRODUCTS.LIST}?${params.toString()}`
          : PRODUCTS.LIST;
      const response = await axiosInstance.get(url);
      const payload = response.data ?? {};
      const items = payload?.data ?? [];
      const meta = payload?.meta ?? {};
      setProducts(items);
      setPagination((prev) => {
        const limitValue = meta.limit ?? prev.limit;
        const offsetValue =
          meta.offset ?? (limitValue ? (prev.page - 1) * limitValue : prev.offset);
        const totalValue = meta.total ?? prev.total;
        const pageValue =
          meta.page ?? (limitValue ? Math.floor(offsetValue / limitValue) + 1 : prev.page);
        const totalPagesValue =
          limitValue && totalValue
            ? Math.max(Math.ceil(totalValue / limitValue), 1)
            : prev.totalPages;
        return {
          ...prev,
          total: totalValue,
          limit: limitValue,
          offset: offsetValue,
          page: pageValue,
          totalPages: totalPagesValue,
        };
      });
    } catch (fetchError) {
      console.error(
        "[ProductCatalogPage] falha ao buscar produtos",
        fetchError
      );
      setError(
        fetchError?.response?.data?.message ||
          "Não foi possível carregar o catálogo de produtos."
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.search,
    filters.type,
    filters.status,
    filters.categoryId,
    sorting.orderBy,
    sorting.order,
    pagination.page,
    pagination.limit,
  ]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const loadCategories = useCallback(async () => {
    setIsLoadingCategories(true);
    try {
      const response = await axiosInstance.get(CATEGORIES.GET_ALL);
      setCategories(response.data?.data ?? []);
    } catch (fetchError) {
      console.error(
        "[ProductCatalogPage] falha ao carregar categorias",
        fetchError
      );
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleOpenModal = (product = null) => {
    setEditingProduct(product);
    setForm(getInitialFormState(product));
    setUnitPriceRaw(
      product?.unitPrice ? digitsFromNumber(product.unitPrice) : ""
    );
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingProduct(null);
    setForm(getInitialFormState());
    setUnitPriceRaw("");
  };

  const handleFormChange = (field, value) => {
    if (field === "unitPrice") {
      const digits = String(value).replace(/[^\d]/g, "");
      setUnitPriceRaw(digits);
      return;
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setNotification({
        type: "danger",
        message: "Nome do produto é obrigatório.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || null,
        categoryId: form.categoryId || null,
        type: form.type,
        description: form.description.trim() || null,
        unitPrice: parseCurrencyDigits(unitPriceRaw),
        unit: form.unit.trim() || null,
        isActive: Boolean(form.isActive),
      };

      if (editingProduct?.id) {
        await axiosInstance.put(PRODUCTS.UPDATE(editingProduct.id), payload);
        setNotification({ type: "success", message: "Produto atualizado." });
        setAnimatedToast({ id: Date.now(), message: "Produto atualizado." });
      } else {
        await axiosInstance.post(PRODUCTS.CREATE, payload);
        setNotification({ type: "success", message: "Produto criado." });
        setAnimatedToast({ id: Date.now(), message: "Produto criado." });
      }

      handleCloseModal();
      loadProducts();
    } catch (submitError) {
      console.error("[ProductCatalogPage] erro ao salvar produto", submitError);
      setNotification({
        type: "danger",
        message:
          submitError?.response?.data?.message ||
          "Não foi possível salvar o produto.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseManageCategoriesModal = () => {
    setManageCategoriesModalOpen(false);
    setCategoryFormName("");
    setEditingCategoryId(null);
    setEditingCategoryName("");
    setCategoryNotification(null);
  };

  const handleOpenCategoryModal = () => {
    setCategoryNotification(null);
    setCategoryFormName("");
    setEditingCategoryId(null);
    setEditingCategoryName("");
    setManageCategoriesModalOpen(true);
    loadCategories();
  };

  const handleCreateCategory = async () => {
    const trimmedName = categoryFormName.trim();
    if (!trimmedName) {
      setCategoryNotification({
        type: "danger",
        message: "Nome da categoria é obrigatório.",
      });
      return;
    }
    setIsCreatingCategory(true);
    try {
      await axiosInstance.post(CATEGORIES.CREATE, { name: trimmedName });
      setAnimatedToast({ id: Date.now(), message: "Categoria criada." });
      setCategoryNotification({
        type: "success",
        message: "Categoria criada.",
      });
      setNotification({ type: "success", message: "Categoria criada." });
      setCategoryFormName("");
      loadCategories();
    } catch (createError) {
      console.error(
        "[ProductCatalogPage] erro ao criar categoria",
        createError
      );
      setCategoryNotification({
        type: "danger",
        message:
          createError?.response?.data?.message ||
          "Não foi possível cadastrar a categoria.",
      });
    } finally {
      setIsCreatingCategory(false);
    }
  };

  const handleStartEditingCategory = (category) => {
    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name);
    setCategoryNotification(null);
  };

  const handleCancelEditingCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryName("");
    setCategoryNotification(null);
  };

  const handleUpdateCategory = async () => {
    if (!editingCategoryId) return;
    const trimmedName = editingCategoryName.trim();
    if (!trimmedName) {
      setCategoryNotification({
        type: "danger",
        message: "Nome da categoria é obrigatório.",
      });
      return;
    }
    setIsUpdatingCategory(true);
    try {
      await axiosInstance.put(CATEGORIES.UPDATE(editingCategoryId), {
        name: trimmedName,
      });
      setAnimatedToast({ id: Date.now(), message: "Categoria atualizada." });
      setCategoryNotification({
        type: "success",
        message: "Categoria atualizada.",
      });
      setNotification({ type: "success", message: "Categoria atualizada." });
      setEditingCategoryId(null);
      setEditingCategoryName("");
      loadCategories();
    } catch (updateError) {
      console.error(
        "[ProductCatalogPage] erro ao atualizar categoria",
        updateError
      );
      setCategoryNotification({
        type: "danger",
        message:
          updateError?.response?.data?.message ||
          "Não foi possível atualizar a categoria.",
      });
    } finally {
      setIsUpdatingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!categoryId) return;
    if (!window.confirm("Deseja remover esta categoria?")) return;
    setIsDeletingCategoryId(categoryId);
    try {
      await axiosInstance.delete(CATEGORIES.DELETE(categoryId));
      setAnimatedToast({ id: Date.now(), message: "Categoria removida." });
      setCategoryNotification({
        type: "success",
        message: "Categoria removida.",
      });
      setNotification({ type: "success", message: "Categoria removida." });
      loadCategories();
    } catch (deleteError) {
      console.error(
        "[ProductCatalogPage] erro ao remover categoria",
        deleteError
      );
      setCategoryNotification({
        type: "danger",
        message:
          deleteError?.response?.data?.message ||
          "Não foi possível remover a categoria.",
      });
    } finally {
      setIsDeletingCategoryId(null);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Deseja remover este produto do catálogo?")) return;
    setIsDeletingId(productId);
    try {
      await axiosInstance.delete(PRODUCTS.DELETE(productId));
      setNotification({ type: "success", message: "Produto removido." });
      setAnimatedToast({ id: Date.now(), message: "Produto removido." });
      loadProducts();
    } catch (deleteError) {
      console.error("[ProductCatalogPage] erro ao apagar produto", deleteError);
      setNotification({
        type: "danger",
        message:
          deleteError?.response?.data?.message ||
          "Não foi possível remover o produto.",
      });
    } finally {
      setIsDeletingId(null);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilterDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSortChange = (field) => {
    setSorting((prev) => {
      const isSameField = prev.orderBy === field;
      const nextOrder = isSameField
        ? prev.order === "asc"
          ? "desc"
          : "asc"
        : "asc";
      return { orderBy: field, order: nextOrder };
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleChangePage = (newPage) => {
    setPagination((prev) => {
      const lastPage = Math.max(prev.totalPages || 1, 1);
      const normalizedPage = Math.max(Math.min(newPage, lastPage), 1);
      return { ...prev, page: normalizedPage };
    });
  };

  const handleApplyFilters = () => {
    setFilters({ ...filterDraft });
    setFiltersModalOpen(false);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    const freshFilters = createProductFilterState();
    setFilters(freshFilters);
    setFilterDraft(freshFilters);
    setSorting(getInitialSortingState());
    setFiltersModalOpen(false);
    setPagination(getInitialPagination);
  };

  const SortableHeader = ({ field, label, align = "left" }) => {
    const isActive = sorting.orderBy === field;
    const justifyClass =
      align === "right"
        ? "justify-end"
        : align === "center"
        ? "justify-center"
        : "justify-start";
    const textAlignClass =
      align === "right"
        ? "text-right"
        : align === "center"
        ? "text-center"
        : "text-left";
    const iconClass = (direction) =>
      isActive && sorting.order === direction
        ? "text-orange-500"
        : "text-slate-400";

    return (
      <th className={`px-4 py-3 ${textAlignClass}`}>
        <button
          type="button"
          className={`inline-flex w-full items-center gap-1 ${justifyClass}`}
          onClick={() => handleSortChange(field)}
          aria-pressed={isActive}
        >
          <span
            className={`text-[11px] uppercase tracking-[0.3em] ${
              isActive ? "text-slate-900" : "text-slate-500"
            }`}
          >
            {label}
          </span>
          <span className="flex text-[13px] leading-none">
            <HiOutlineArrowSmUp className={`${iconClass("asc")} -mr-1`} />
            <HiOutlineArrowSmDown className={iconClass("desc")} />
          </span>
        </button>
      </th>
    );
  };
  const limitForRange = pagination.limit || DEFAULT_PAGE_LIMIT;
  const offsetForRange =
    pagination.offset !== undefined
      ? pagination.offset
      : (pagination.page - 1) * limitForRange;
  const totalItems = pagination.total ?? 0;
  const rangeStart = totalItems > 0 ? offsetForRange + 1 : 0;
  const rangeEnd =
    totalItems > 0 ? Math.min(offsetForRange + limitForRange, totalItems) : 0;
  const canPrevious = pagination.page > 1;
  const canNext = pagination.page < Math.max(pagination.totalPages || 1, 1);

  const handleOpenProductMenu = (product) => {
    setActiveProduct(product);
    setProductMenuOpen(true);
  };

  const handleCloseProductMenu = () => {
    setProductMenuOpen(false);
    setActiveProduct(null);
  };

  const handleMenuEdit = () => {
    if (!activeProduct) return;
    handleCloseProductMenu();
    handleOpenModal(activeProduct);
  };

  const handleMenuDelete = () => {
    if (!activeProduct) return;
    handleCloseProductMenu();
    handleDelete(activeProduct.id);
  };

  useEffect(() => {
    if (!productMenuOpen) {
      setProductMenuSlideIn(false);
      return;
    }
    requestAnimationFrame(() => {
      setProductMenuSlideIn(true);
    });
  }, [productMenuOpen]);

  const statusBadge = useMemo(
    () => (product) =>
      (
        <Badge
          className="w-fit mx-auto"
          color={product.isActive ? "success" : "light"}
        >
          {product.isActive ? "Ativo" : "Inativo"}
        </Badge>
      ),
    []
  );

  const getCategoryLabel = (product) =>
    product?.category?.name ?? product?.categoryName ?? "-";

  return (
    <div className="space-y-4 pb-14">
      <header className="flex flex-col gap-0">
        <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
          Catálogo
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Produtos e serviços do catálogo
            </h1>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="hidden md:flex items-center gap-2">
              <Button onClick={() => handleOpenModal()} size="sm">
                Novo produto
              </Button>
              <Button onClick={handleOpenCategoryModal} size="sm" color="light">
                Gerenciar categorias
              </Button>
            </div>
            <div className="w-full md:hidden">
              <Button
                size="sm"
                color="light"
                className="w-full"
                onClick={handleOpenCategoryModal}
              >
                Gerenciar categorias
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
        <div className="p-4 space-y-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="flex-1">
              <div className="relative">
                <IoMdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 pl-10 text-sm focus:border-gray-300 focus:ring-0"
                  placeholder="Buscar por nome ou código"
                  value={filterDraft.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="hidden md:flex gap-4">
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Categoria
              </label>
              <select
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                value={filterDraft.categoryId}
                onChange={(e) =>
                  handleFilterChange("categoryId", e.target.value)
                }
              >
                <option value="">Todas</option>
                {isLoadingCategories ? (
                  <option value="" disabled>
                    Carregando categorias...
                  </option>
                ) : (
                  categories.map((category) => (
                    <option key={category.id} value={`${category.id}`}>
                      {category.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Tipo
              </label>
              <select
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                value={filterDraft.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
              >
                <option value="all">Todos</option>
                <option value="Produto">Produto</option>
                <option value="Serviço">Serviço</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Status
              </label>
              <select
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                value={filterDraft.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="all">Todos</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <Button size="sm" onClick={handleApplyFilters}>
              Filtrar
            </Button>
            <Button size="sm" color="light" onClick={handleResetFilters}>
              Limpar
            </Button>

            <Button
              size="sm"
              color="light"
              className="md:hidden"
              onClick={() => setFiltersModalOpen(true)}
            >
              <HiFilter className="text-lg" />
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        {error && (
          <div className="mb-4 rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-700">
            {error}
          </div>
        )}
        <div className="hidden md:block">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-slate-700">
              <thead className="text-[11px] uppercase tracking-[0.3em] text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Código</th>
                  <SortableHeader field="name" label="Nome" align="left" />
                  <SortableHeader
                    field="category"
                    label="Categoria"
                    align="left"
                  />
                  <SortableHeader field="type" label="Tipo" align="left" />
                  <SortableHeader
                    field="unitPrice"
                    label="Valor Unit."
                    align="right"
                  />
                  <SortableHeader
                    field="status"
                    label="Status"
                    align="center"
                  />
                  <th className="px-4 py-3 text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  skeletonRows.map((_, index) => (
                    <tr
                      key={`skeleton-${index}`}
                      className="border-t border-slate-100 last:border-b"
                    >
                      <td className="px-4 py-4">
                        <div className="h-4 w-3/4 rounded-full bg-slate-200" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-1/2 rounded-full bg-slate-200" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-2/3 rounded-full bg-slate-200" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-1/2 rounded-full bg-slate-200" />
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="h-4 w-1/2 self-end rounded-full bg-slate-200" />
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="inline-block h-3 w-12 rounded-full bg-slate-200" />
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="h-6 w-6 rounded-full bg-slate-200" />
                          <span className="h-6 w-6 rounded-full bg-slate-200" />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : products.length ? (
                  products.map((product) => (
                    <tr
                      key={product.id}
                      className="border-t border-slate-100 last:border-b"
                    >
                      <td className="px-4 py-3 text-slate-600">
                        {product.code ? `#${product.code}` : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">
                          {product.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {getCategoryLabel(product)}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {product.type}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                        {formatCurrency(product.unitPrice)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {statusBadge(product)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          <Button
                            size="xs"
                            color="gray"
                            onClick={() => handleOpenModal(product)}
                            className="flex items-center gap-1"
                          >
                            <HiOutlinePencil className="text-lg" />
                          </Button>
                          <Button
                            size="xs"
                            color="failure"
                            onClick={() => handleDelete(product.id)}
                            disabled={isDeletingId === product.id}
                            className="flex items-center gap-1"
                          >
                            <HiOutlineTrash className="text-lg" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-sm text-slate-500"
                    >
                      Nenhum produto cadastrado ainda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-2 border-t border-slate-100 px-3 py-2 text-xs text-slate-500 md:flex-row md:items-center md:justify-between">
          <div>
            {totalItems
              ? `Mostrando ${rangeStart}-${rangeEnd} de ${totalItems}`
              : "Nenhum produto cadastrado ainda."}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="xs"
              color="light"
              onClick={() => handleChangePage(pagination.page - 1)}
              disabled={!canPrevious}
            >
              Anterior
            </Button>
            <Button
              size="xs"
              color="light"
              onClick={() => handleChangePage(pagination.page + 1)}
              disabled={!canNext}
            >
              Próximo
            </Button>
          </div>
        </div>

        <div className="md:hidden space-y-3 mt-4">
          {isLoading ? (
            skeletonRows.map((_, index) => (
              <div
                key={`mobile-skeleton-${index}`}
                className="animate-pulse rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
              >
                <div className="h-4 w-1/2 rounded-full bg-slate-200" />
                <div className="mt-3 flex items-center justify-between">
                  <div className="h-3 w-1/3 rounded-full bg-slate-200" />
                  <div className="h-3 w-1/3 rounded-full bg-slate-200" />
                </div>
                <div className="mt-3 h-3 w-2/4 rounded-full bg-slate-200" />
              </div>
            ))
          ) : products.length ? (
            products.map((product) => (
              <button
                key={product.id}
                type="button"
                className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm"
                onClick={() => handleOpenProductMenu(product)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">
                      {product.name}
                    </div>
                    {product.code && (
                      <div className="text-xs text-slate-500">
                        #{product.code}
                      </div>
                    )}
                    <div className="text-xs text-slate-500">
                      {getCategoryLabel(product)}
                    </div>
                  </div>
                  <HiOutlineArrowRight className="text-slate-400" />
                </div>
                <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
                  <span>{product.type}</span>
                  <span className="font-semibold text-slate-900">
                    {formatCurrency(product.unitPrice)}
                  </span>
                </div>
              </button>
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              Nenhum produto cadastrado ainda.
            </div>
          )}
        </div>
      </div>

      <Modal
        show={filtersModalOpen}
        size="sm"
        popup
        onClose={() => setFiltersModalOpen(false)}
      >
        <Modal.Header>
          <div className="text-base px-2">Filtros</div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4 text-sm">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Categoria
              </label>
              <select
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={filterDraft.categoryId}
                onChange={(e) =>
                  handleFilterChange("categoryId", e.target.value)
                }
              >
                <option value="">Todas</option>
                {isLoadingCategories ? (
                  <option value="" disabled>
                    Carregando categorias...
                  </option>
                ) : (
                  categories.map((category) => (
                    <option key={category.id} value={`${category.id}`}>
                      {category.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Tipo
              </label>
              <select
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={filterDraft.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
              >
                <option value="all">Todos</option>
                <option value="Produto">Produto</option>
                <option value="Serviço">Serviço</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Status
              </label>
              <select
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={filterDraft.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="all">Todos</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                color="light"
                onClick={() => setFiltersModalOpen(false)}
              >
                Fechar
              </Button>
              <Button size="sm" color="success" onClick={handleApplyFilters}>
                Aplicar
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={modalOpen} size="xl" popup onClose={handleCloseModal}>
        <Modal.Header>
          <div className="text-base px-2">
            {editingProduct ? "Editar produto" : "Novo produto"}
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4 text-sm">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Nome
              </label>
              <input
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={form.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Código
              </label>
              <input
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={form.code}
                onChange={(e) => handleFormChange("code", e.target.value)}
              />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Tipo
                </label>
                <select
                  className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={form.type}
                  onChange={(e) => handleFormChange("type", e.target.value)}
                >
                  <option value="Produto">Produto</option>
                  <option value="Serviço">Serviço</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Valor unitário
                </label>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  type="text"
                  inputMode="decimal"
                  value={formatDigitsToCurrency(unitPriceRaw)}
                  onChange={(e) =>
                    handleFormChange("unitPrice", e.target.value)
                  }
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Categoria
              </label>
              <select
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={form.categoryId}
                onChange={(e) => handleFormChange("categoryId", e.target.value)}
                disabled={isLoadingCategories}
              >
                <option value="">Selecione uma categoria</option>
                {isLoadingCategories ? (
                  <option value="" disabled>
                    Carregando categorias...
                  </option>
                ) : (
                  categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))
                )}
              </select>
              {!isLoadingCategories && categories.length === 0 && (
                <p className="mt-1 text-xs text-slate-500">
                  Cadastre uma categoria antes de utilizar este campo.
                </p>
              )}
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Descrição
              </label>
              <textarea
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                rows={3}
                value={form.description}
                onChange={(e) =>
                  handleFormChange("description", e.target.value)
                }
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => handleFormChange("isActive", e.target.checked)}
              />
              <span className="text-sm text-slate-600">Ativo</span>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button color="gray" size="sm" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting
                ? "Salvando..."
                : editingProduct
                ? "Salvar alterações"
                : "Criar produto"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={manageCategoriesModalOpen}
        size="2xl"
        popup
        onClose={handleCloseManageCategoriesModal}
      >
        <Modal.Header>
          <div className="text-base px-2">Gerenciar categorias</div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4 text-sm">
            <div className="space-y-2 rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Nova categoria
              </label>
              <div className="flex items-center gap-1 mt-1">
                <input
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={categoryFormName}
                  onChange={(e) => setCategoryFormName(e.target.value)}
                  placeholder="Digite o nome da categoria"
                />
                <Button
                  size="sm"
                  color="success"
                  onClick={handleCreateCategory}
                  disabled={isCreatingCategory}
                >
                  {isCreatingCategory ? "Criando..." : "Criar"}
                </Button>
              </div>
            </div>
            <div className="space-y-3 max-h-[280px] overflow-y-auto">
              {isLoadingCategories ? (
                <div className="text-xs text-slate-500">
                  Carregando categorias...
                </div>
              ) : categories.length ? (
                categories.map((category) => {
                  const isEditing = editingCategoryId === category.id;
                  return (
                    <div
                      key={category.id}
                      className="flex flex-col gap-2 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm md:flex-row md:items-center md:gap-3"
                    >
                      <div className="flex-1">
                        {isEditing ? (
                          <input
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            value={editingCategoryName}
                            onChange={(e) =>
                              setEditingCategoryName(e.target.value)
                            }
                          />
                        ) : (
                          <p className="text-sm font-semibold text-slate-900">
                            {category.name}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {isEditing ? (
                          <>
                            <Button
                              size="xs"
                              color="success"
                              onClick={handleUpdateCategory}
                              disabled={isUpdatingCategory}
                            >
                              {isUpdatingCategory ? "Salvando..." : "Salvar"}
                            </Button>
                            <Button
                              size="xs"
                              color="light"
                              onClick={handleCancelEditingCategory}
                            >
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="xs"
                              color="gray"
                              onClick={() =>
                                handleStartEditingCategory(category)
                              }
                            >
                              Editar
                            </Button>
                            <Button
                              size="xs"
                              color="failure"
                              onClick={() => handleDeleteCategory(category.id)}
                              disabled={isDeletingCategoryId === category.id}
                            >
                              Excluir
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-xs text-slate-500">
                  Nenhuma categoria cadastrada ainda. Crie uma nova categoria
                  abaixo.
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-center w-full">
            <Button
              color="gray"
              size="sm"
              onClick={handleCloseManageCategoriesModal}
            >
              Fechar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <AnimatedToast
        open={Boolean(animatedToast)}
        message={animatedToast?.message}
        onClose={() => setAnimatedToast(null)}
      />

      <Button
        onClick={() => handleOpenModal()}
        className="fixed right-4 bottom-6 z-30 flex h-14 w-14 items-center justify-center rounded-full p-0 text-2xl font-semibold shadow-lg md:hidden"
      >
        <HiOutlinePlus className="text-2xl" />
      </Button>

      {productMenuOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 z-0 bg-black opacity-50"
            onClick={handleCloseProductMenu}
          />
          <div
            className={`relative z-10 w-full rounded-t-xl bg-white px-6 pt-3 pb-8 shadow-[0_0_24px_rgba(15,23,42,0.25)] transition-transform duration-300 ease-out transform ${
              productMenuSlideIn ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-slate-200"></div>
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                Opções rápidas
              </p>
              <div className="space-y-1">
                <p className="text-lg font-semibold text-slate-900">
                  {activeProduct?.name}
                </p>
                {activeProduct?.code && (
                  <p className="text-sm text-slate-500">
                    #{activeProduct.code}
                  </p>
                )}
              </div>
              <div className="space-y-3">
                <Button
                  color="success"
                  fullWidth
                  className="rounded-2xl py-3 text-base font-semibold w-full"
                  onClick={handleMenuEdit}
                >
                  Editar produto
                </Button>
                <Button
                  color="failure"
                  fullWidth
                  className="rounded-2xl py-3 text-base font-semibold w-full"
                  onClick={handleMenuDelete}
                >
                  Excluir produto
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCatalogPage;
